import { skipToken } from "@reduxjs/toolkit/query";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { populateDocuments } from "documents/_stores/documents-redux-slice";
import { populateEntityAgreements } from "legal-entities/agreements/_stores/entity-agreements-redux-slice";
import _ from "lodash";
import { populateIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import { populateForums } from "messaging/forums/_stores/forums-redux-slice";
import PermissionsByGrantee from "permissions/by-grantee/permissions-by-grantee";
import { DocumentAccess } from "permissions/resources/documents/documents-permissions-resources";
import { EntityAgreementAccess } from "permissions/resources/legal-entities/legal-entities-permissions-resources";
import { ForumAccess } from "permissions/resources/messaging/messaging-permissions-resources";
import { ProposalAccess, WorkAgreementAccess } from "permissions/resources/work/work-permissions-resources";
import { useEffect, useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { useGetProposalsByIdsQuery } from "work/proposals/_api/proposal-api";
import { populateBarebonesWorkAgreements } from "work/work-agreements/_store/work-agreement-redux-slice";

/**
 * Hook: usePopulateRelatedEntities
 *
 * Consumes a permissions response and dispatches populate actions
 * for all related entity types (individuals, documents, forums, proposals, work agreements, entity agreements).
 *
 * Returns an `isRefetching` flag that is true while dispatching occurs.
 */
export default function usePopulateRelatedEntities(permissionsResponse?: PaginatedResponse<PermissionsByGrantee>): {
  isRefetching: boolean;
  userIds: Guid[];
  documentIds: Guid[];
  forumIds: Guid[];
  proposalIds: Guid[];
  workAgreementIds: Guid[];
  entityAgreementIds: Guid[];
} {
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();

  const [isRefetching, setIsRefetching] = useState(false);

  // Compute IDs for lookups
  const { userIds, documentIds, forumIds, proposalIds, workAgreementIds, entityAgreementIds } = useMemo(() => {
    const isGuid = (id: Guid | undefined | null): id is Guid => !!id && !id.isEqualTo(Guid.EMPTY);

    const data = permissionsResponse?.data ?? [];
    const permissions = data.flatMap((g) => g.recentPermissionsPreview ?? []);

    const _uniqueGranteeIds = _.uniqBy(data.map((g) => g.granteeUserId).filter(isGuid), (id) => id.value);
    const _uniqueGrantorIds = _.uniqBy(permissions.map((p) => p.grantorUserId).filter(isGuid), (id) => id.value);
    const _documentIds = _.uniqBy(
      permissions
        .filter((p) => p.resource instanceof DocumentAccess)
        .map((p) => (p.resource as DocumentAccess).documentId)
        .filter(isGuid),
      (id) => id.value,
    );
    const _forumIds = _.uniqBy(
      permissions
        .filter((p) => p.resource instanceof ForumAccess)
        .map((p) => (p.resource as ForumAccess).forumId)
        .filter(isGuid),
      (id) => id.value,
    );
    const _proposalIds = _.uniqBy(
      permissions
        .filter((p) => p.resource instanceof ProposalAccess)
        .map((p) => (p.resource as ProposalAccess).proposalId)
        .filter(isGuid),
      (id) => id.value,
    );
    const _workAgreementIds = _.uniqBy(
      permissions
        .filter((wa) => wa.resource instanceof WorkAgreementAccess)
        .map((wa) => (wa.resource as WorkAgreementAccess).workAgreementId)
        .filter(isGuid),
      (id) => id.value,
    );
    const _entityAgreementIds = _.uniqBy(
      permissions
        .filter((ea) => ea.resource instanceof EntityAgreementAccess)
        .map((ea) => (ea.resource as EntityAgreementAccess).entityAgreementId)
        .filter(isGuid),
      (id) => id.value,
    );

    const _uniqueUserIds = _.uniqBy([..._uniqueGranteeIds, ..._uniqueGrantorIds], (id) => id.value);

    return {
      uniqueGranteeIds: _uniqueGranteeIds,
      uniqueGrantorIds: _uniqueGrantorIds,
      userIds: _uniqueUserIds,
      documentIds: _documentIds,
      forumIds: _forumIds,
      proposalIds: _proposalIds,
      workAgreementIds: _workAgreementIds,
      entityAgreementIds: _entityAgreementIds,
    };
  }, [permissionsResponse]);

  useGetProposalsByIdsQuery(proposalIds?.length ? { proposalIds: proposalIds.map((id) => id.value) } : skipToken);
  useEffect(() => {
    if (!permissionsResponse) return;

    setIsRefetching(true);

    const promises = [
      dispatch(populateIndividualsByUserIds({ userIds })),
      dispatch(populateDocuments({ session, documentIds })),
      dispatch(populateForums({ session, forumIds })),
      dispatch(populateBarebonesWorkAgreements({ session, workAgreementIds })),
      dispatch(populateEntityAgreements({ session, entityAgreementIds })),
    ];

    let active = true;

    (async () => {
      try {
        await Promise.allSettled(promises as Array<Promise<unknown>>);
      } finally {
        if (active) setIsRefetching(false);
      }
    })();
  }, [dispatch, session, userIds, documentIds, forumIds, proposalIds, workAgreementIds, entityAgreementIds, permissionsResponse]);

  return {
    isRefetching,
    userIds,
    documentIds,
    forumIds,
    proposalIds,
    workAgreementIds,
    entityAgreementIds,
  };
}
